/**
 * Sprint 23.7 — `<WorkforceEmptyState>`: the big first-impression moment shown on
 * `/workforce` when the roster is empty. Composes `<GhostAvatarRow>` + themed
 * heading/subheading + a primary "Hire your first agent" CTA + an "or start
 * from" divider + a 2×2 grid of `<StarterChip>`s. Visual target:
 * `workforce_empty_state_mockup_v1`.
 *
 * The primary CTA opens a blank Hire wizard; each concrete starter pre-fills it
 * with an intent string (`/hire?prefill=…`); "Describe your own" opens it blank.
 */

import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { GhostAvatarRow } from "../primitives";
import { StarterChip } from "./StarterChip";
import type { ThemedLabels } from "../../lib/themed-labels";

interface StarterDef {
  icon: string;
  label: string;
  description: string;
  ramp: "purple" | "teal" | "coral" | "neutral";
  dashed?: boolean;
  intent?: string;
}

const STARTERS: StarterDef[] = [
  {
    icon: "search",
    label: "Sales research",
    description: "Inbound lead verification",
    ramp: "purple",
    intent: "Research and verify inbound sales leads from my pipeline.",
  },
  {
    icon: "pencil",
    label: "Content strategy",
    description: "Posts, calendars, briefs",
    ramp: "teal",
    intent: "Plan content strategy — social posts, content calendars, and briefs.",
  },
  {
    icon: "package",
    label: "Operations",
    description: "Admin, scheduling, ops",
    ramp: "coral",
    intent: "Handle operations — admin, scheduling, and day-to-day ops.",
  },
  {
    icon: "sparkles",
    label: "Describe your own",
    description: "Tell me what you need",
    ramp: "neutral",
    dashed: true,
  },
];

export function WorkforceEmptyState({ labels }: { labels: ThemedLabels }) {
  const navigate = useNavigate();

  const openHire = (intent?: string) =>
    navigate(intent ? `/hire?prefill=${encodeURIComponent(intent)}` : "/hire");

  return (
    <div style={containerStyle}>
      <div style={blockStyle}>
        <GhostAvatarRow count={5} />

        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {labels.emptyTitle}
        </h2>
        <p style={subtitleStyle}>{labels.emptySubtitle}</p>

        <button type="button" onClick={() => openHire()} style={primaryCtaStyle}>
          <i className="ti ti-plus" style={{ fontSize: 14 }} aria-hidden="true" />
          {labels.emptyCta}
          <i className="ti ti-arrow-right" style={{ fontSize: 14 }} aria-hidden="true" />
        </button>

        <div style={dividerStyle}>
          <span style={dividerLineStyle} />
          <span style={dividerTextStyle}>or start from</span>
          <span style={dividerLineStyle} />
        </div>

        <div style={gridStyle}>
          {STARTERS.map((s) => (
            <StarterChip
              key={s.label}
              icon={s.icon}
              label={s.label}
              description={s.description}
              ramp={s.ramp}
              dashed={s.dashed}
              onClick={() => openHire(s.intent)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const containerStyle: CSSProperties = {
  background: "var(--color-background-tertiary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 20px",
  minHeight: 420,
  fontFamily: "var(--font-sans)",
};

const blockStyle: CSSProperties = {
  maxWidth: 420,
  width: "100%",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 18,
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: "var(--color-text-secondary)",
  lineHeight: 1.6,
  maxWidth: 360,
};

const primaryCtaStyle: CSSProperties = {
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 500,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  border: "none",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dividerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  marginTop: 4,
};

const dividerLineStyle: CSSProperties = {
  flex: 1,
  height: "0.5px",
  background: "var(--color-border-tertiary)",
};

const dividerTextStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  width: "100%",
};
